import { useMemo } from "react";
import { useSubscription } from "@/hooks/useSubscription";
import { useSubscriptionPreapproval, type PreapprovalRow } from "@/hooks/useSubscriptionPreapproval";
import { usePlans, Plan } from "@/hooks/usePlans";
import { differenceInCalendarDays, addDays, format } from "date-fns";
import { es } from "date-fns/locale";
import { planGraceDays } from "../../supabase/functions/_shared/plan-billing-frequency";
import { bogotaToday, prepaidExpiry } from "../../supabase/functions/_shared/prepaid-expiry";
import { subscriptionEnd, type SubscriptionEnd } from "@/lib/subscription-end";
import { billingDay, billingDayAsLocalDate } from "@/lib/billing-day";

export type BillingState = "today" | "overdue-grace" | "overdue-expired" | "upcoming" | "none" | "prepaid-expired" | "prepaid-expiring";

export interface BillingStatus {
  /** Whether this org has a custom plan */
  isCustomPlan: boolean;
  /** The resolved plan object */
  currentPlan: Plan | null;
  /** Billing state derived from next_billing_date */
  billingState: BillingState;
  /** Whether the org is missing a payment method */
  missingPaymentMethod: boolean;
  /** Days overdue (0 if not overdue) */
  daysOverdue: number;
  /** The next billing date as a local calendar date (timezone-safe) */
  nextBillingDate: Date | null;
  /** Grace deadline date */
  graceDeadline: Date | null;
  /** Loading state */
  loading: boolean;
  /** Formatted date string */
  formatDate: (d: Date) => string;
  /** Legacy grace_period_ends_at from subscription */
  legacyGracePeriodEndsAt: string | null;
  subscriptionEnd: SubscriptionEnd;
  /** Has an active subscription */
  hasSubscription: boolean;
  /** Prepaid plan expiration date */
  expiresAt: Date | null;
  /** Whether this is a prepaid plan (has expires_at) */
  isPrepaid: boolean;
  /** Days until prepaid expiration (negative = already expired) */
  daysUntilExpiry: number;
  preapproval: PreapprovalRow | null;
  nextBillingDay: string | null;
}

/**
 * Parse an ISO date string into a local calendar date,
 * extracting only the YYYY-MM-DD part to avoid timezone shifts.
 */
function parseCalendarDate(iso: string): Date {
  const dateOnly = iso.substring(0, 10); // "YYYY-MM-DD"
  const [y, m, d] = dateOnly.split("-").map(Number);
  return new Date(y, m - 1, d); // local midnight
}

/**
 * Get today as a local midnight date for consistent calendar-day comparison.
 */
function getToday(): Date {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
}

export function useBillingStatus(
  orgId: string | null,
  organizationPlanId: string | null
): BillingStatus {
  const { subscription, loading: subLoading } = useSubscription(orgId);
  // `missingPaymentMethod` keeps its legacy name; source moved to mp_preapprovals.
  const { preapproval, loading: preapprovalLoading } = useSubscriptionPreapproval(orgId);
  const { plans, loading: plansLoading } = usePlans(true);

  return useMemo(() => {
    const loading = subLoading || preapprovalLoading || plansLoading;

    if (loading) {
      return {
        isCustomPlan: false,
        currentPlan: null,
        billingState: "none",
        missingPaymentMethod: false,
        daysOverdue: 0,
        nextBillingDate: null,
        graceDeadline: null,
        loading: true,
        formatDate: (d: Date) => format(d, "d 'de' MMMM, yyyy", { locale: es }),
        legacyGracePeriodEndsAt: null,
        subscriptionEnd: { kind: "none" },
        hasSubscription: false,
        expiresAt: null,
        isPrepaid: false,
        daysUntilExpiry: 0,
        preapproval: null,
        nextBillingDay: null,
      };
    }

    const resolvedPlanId = organizationPlanId || subscription?.plan_id || subscription?.plan_type || null;
    const currentPlan = plans.find((p) => p.id === resolvedPlanId) || null;
    const isCustomPlan = currentPlan?.is_custom === true;
    const needsPreapproval =
      currentPlan?.payment_strategy === "preapproval"
      && (currentPlan?.price_monthly ?? 0) > 0;
    const missingPaymentMethod = needsPreapproval && !preapproval;
    const fmtDate = (d: Date) => format(d, "d 'de' MMMM, yyyy", { locale: es });

    // Prepaid plan logic
    const isPrepaid = !!subscription?.expires_at;
    let expiresAt: Date | null = null;
    let daysUntilExpiry = 0;

    let billingState: BillingState = "none";
    let daysOverdue = 0;
    let nextBillingDate: Date | null = null;
    let graceDeadline: Date | null = null;

    const expiry = prepaidExpiry(subscription?.expires_at, currentPlan, bogotaToday());
    if (isPrepaid && expiry.expiresOn && expiry.daysLeft !== null) {
      expiresAt = parseCalendarDate(expiry.expiresOn);
      daysUntilExpiry = expiry.daysLeft;

      if (expiry.phase === "expired") {
        billingState = "prepaid-expired";
        daysOverdue = Math.abs(daysUntilExpiry);
      } else if (expiry.phase === "grace") {
        billingState = "prepaid-expiring";
        daysOverdue = Math.abs(daysUntilExpiry);
        graceDeadline = expiry.graceUntil ? parseCalendarDate(expiry.graceUntil) : null;
      } else if (daysUntilExpiry <= 15) {
        billingState = "prepaid-expiring";
      } else {
        billingState = "upcoming";
      }
    } else if (subscription?.next_billing_date) {
      nextBillingDate = billingDayAsLocalDate(subscription.next_billing_date) ?? parseCalendarDate(subscription.next_billing_date);
      const today = getToday();
      const diffDays = differenceInCalendarDays(today, nextBillingDate);
      const graceDays = planGraceDays(currentPlan);

      if (diffDays === 0) {
        billingState = "today";
      } else if (diffDays > 0 && diffDays <= graceDays) {
        billingState = "overdue-grace";
        daysOverdue = diffDays;
      } else if (diffDays > graceDays) {
        billingState = "overdue-expired";
        daysOverdue = diffDays;
      } else {
        billingState = "upcoming";
      }

      if (billingState === "overdue-grace" || billingState === "overdue-expired") {
        graceDeadline = addDays(nextBillingDate, graceDays);
      }
    }

    return {
      isCustomPlan,
      currentPlan,
      billingState,
      missingPaymentMethod,
      daysOverdue,
      nextBillingDate,
      graceDeadline,
      loading,
      formatDate: fmtDate,
      legacyGracePeriodEndsAt: subscription?.grace_period_ends_at || null,
      subscriptionEnd: subscriptionEnd(subscription, preapproval),
      hasSubscription: !!subscription,
      expiresAt,
      isPrepaid,
      daysUntilExpiry,
      preapproval,
      nextBillingDay: billingDay(subscription?.next_billing_date),
    };
  }, [subscription, preapproval, plans, orgId, organizationPlanId, subLoading, preapprovalLoading, plansLoading]);
}
